import { useUser } from "@/hooks/useUser";
import { PRICING } from "@/config/products";
import { useCustomer } from "autumn-js/react";
import { Progress } from "@/components/ui/progress";

type UsageMetric = {
  label: string;
  count: number;
  cost: number;
};

type UsageStatisticsProps = {
  showAllMetrics?: boolean;
};

export const UsageStatistics = ({ showAllMetrics = false }: UsageStatisticsProps) => {
  const { user, hasPro } = useUser();
  const { customer } = useCustomer();

  const basicCount = user?.standardAiSuggestions || 0;
  const advancedCount = user?.proModelAiSuggestions || 0;


  const metrics: UsageMetric[] = [
    {
      label: "Basic Requests",
      count: basicCount,
      cost: basicCount * PRICING.BASE_REQUEST_PRICE,
    },
    {
      label: "Pro Requests",
      count: advancedCount,
      cost: advancedCount * PRICING.PRO_REQUEST_PRICE,
    },
  ];

  const renderUsageItem = (metric: UsageMetric) => {
    return (
      <div className="flex justify-between items-center text-sm">
        <span className="text-muted-foreground">{metric.label}</span>
        <div className="flex items-center gap-2">
          <span className="font-medium">{metric.count}</span>
          <span className="text-muted-foreground">({metric.cost * 100})</span>
        </div>
      </div>
    );
  };

  if (showAllMetrics) {
    return (
      <div className="space-y-4">
        {metrics.map((metric) => (
          <div key={metric.label}>{renderUsageItem(metric)}</div>
        ))}
      </div>
    );
  }

  // Calculate total credits and usage
  const getTotalCredits = () => {
    let totalBalance = 0;
    let totalUsage = 0;
    let totalIncluded = 0;

    if (!hasPro && customer?.features?.credit_free) {
      totalBalance += customer.features.credit_free.balance || 0;
      totalUsage += customer.features.credit_free.usage || 0;
      totalIncluded += customer.features.credit_free.included_usage || 0;
    }

    if (hasPro && customer?.features?.credit_pro) {
      totalBalance += customer.features.credit_pro.balance || 0;
      totalUsage += customer.features.credit_pro.usage || 0;
      totalIncluded += customer.features.credit_pro.included_usage || 0;
    }

    if (customer?.features?.credit_top_up) {
      totalBalance += customer.features.credit_top_up.balance || 0;
      totalUsage += customer.features.credit_top_up.usage || 0;
      totalIncluded += customer.features.credit_top_up.included_usage || 0;
    }

    return { totalBalance, totalUsage, totalIncluded };
  };

  const { totalBalance, totalUsage, totalIncluded } = getTotalCredits();
  const usagePercentage = totalIncluded > 0 ? (totalUsage / totalIncluded) * 100 : 0;

  return (
    <div className="w-full space-y-1.5 select-none my-2">
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Available Credits</span>
          <span className="text-foreground">{totalBalance}</span>
        </div>
        <Progress value={usagePercentage} className="h-1.5" />
      </div>
    </div>
  );
};
